import { useCallback } from 'react';
import { useNotificationContext } from '@/features/notifications';
import {
  useCreateLabel,
  useGetOrganizationLabels,
  useDeleteLabel,
  useAssignLabelToFile,
  useRemoveLabelFromFile,
} from '@/shared/api';
import { useDataInvalidation } from '@/shared/hooks';
import { useRouterContext } from '@/shared/router';

export const useManageTagsData = () => {
  const { activeOrganization } = useRouterContext();
  const { showNotification } = useNotificationContext();
  const invalidate = useDataInvalidation();

  const { data: labels } = useGetOrganizationLabels(
    { pathParams: { organizationId: activeOrganization?.id || '' } },
    { enabled: Boolean(activeOrganization?.id) },
  );

  const createLabelMutation = useCreateLabel();
  const deleteLabelMutation = useDeleteLabel();
  const assignLabelMutation = useAssignLabelToFile();
  const removeLabelMutation = useRemoveLabelFromFile();

  const onLabelSelect = useCallback(
    async (fileId: string, labelId: string) => {
      try {
        await assignLabelMutation.mutateAsync({
          pathParams: { fileId, labelId },
        });
        invalidate('files');
      } catch {
        showNotification({
          type: 'error',
          message: 'Error',
          description: 'Failed to assign tag',
        });
      }
    },
    [assignLabelMutation, invalidate, showNotification],
  );

  const onLabelDeselect = useCallback(
    async (fileId: string, labelId: string) => {
      try {
        await removeLabelMutation.mutateAsync({
          pathParams: { fileId, labelId },
        });
        invalidate('files');
      } catch {
        showNotification({
          type: 'error',
          message: 'Error',
          description: 'Failed to remove tag',
        });
      }
    },
    [removeLabelMutation, invalidate, showNotification],
  );

  const onLabelCreate = useCallback(
    async (name: string) => {
      try {
        const response = await createLabelMutation.mutateAsync({
          body: {
            name: name.trim(),
            organizationId: activeOrganization?.id || '',
          },
        });
        invalidate('files');
        invalidate('file-labels');
        showNotification({
          type: 'success',
          message: 'Success',
          description: 'Tag created successfully',
        });
        return response;
      } catch (error) {
        showNotification({
          type: 'error',
          message: 'Error',
          description: 'Failed to create tag',
        });
        throw error;
      }
    },
    [activeOrganization?.id, invalidate, createLabelMutation, showNotification],
  );

  const onLabelDelete = useCallback(
    async (labelId: string) => {
      try {
        await deleteLabelMutation.mutateAsync({
          pathParams: { id: labelId },
        });
        invalidate('files');
        invalidate('file-labels');
        showNotification({
          type: 'success',
          message: 'Success',
          description: 'Tag deleted successfully',
        });
      } catch {
        showNotification({
          type: 'error',
          message: 'Error',
          description: 'Failed to delete tag',
        });
      }
    },
    [deleteLabelMutation, invalidate, showNotification],
  );

  return {
    labels,
    onLabelSelect,
    onLabelDeselect,
    onLabelCreate,
    onLabelDelete,
  };
};
